import React, { useState, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { Formik } from 'formik';
import Common from 'us.common';
import { Link, matchPath } from 'react-router-dom';
import { INavigationData } from 'us.collection/interfaces';
import { IOnFilter, IOnSort } from 'us.common/components/antd/TableTree';
import { useHistory } from 'react-router-dom';
import { PlusOutlined } from 'us.icons';
import { ManageCreditorInformation } from 'us.collection.creditor/components/ManageCreditors';
import './Style.scss';
import { RootState } from 'us.helper/types';
import { $Popconfirm, $TextEditor } from 'us.common/components';
import * as Actions from 'us.collection.creditor/actions';
import { CreditorGroupAuthInfoOverrideLeval } from '../Constants';
import { SaveCreditorAuthorizedInformation } from '../Repository';
import { CreditorInfoTextEditorModules } from 'us.collection.creditor/constants';

const { $PageHeader, $Affix, $Skeleton, $TableTree, $Button, $Drawer } =
	Common.Components;

interface ISelectedCreditorsHome {
	isCreditorManagement?: boolean;
	drawerContentChangeClick?: any;
}

export interface ISortOrder {
	columnKey: string;
	order: any;
}

const SelectedCreditorsHome: React.FC<ISelectedCreditorsHome & PropsFromRedux> =
	memo((props) => {
		const { t } = useTranslation();
		const { push, location } = useHistory();
		const state = location.state as INavigationData;

		const { params }: any = matchPath(window.location.pathname, {
			path: '/:type/:id?/:module?',
			exact: false,
			strict: false,
		});

		const {
			selectedCreditors,
			metaData,
			isCreditorManagement,
			drawerContentChangeClick,
			creditorGroupData,
			addCreditorAuthorizedInformation
		} = props;

		const { creditorGroupId } = metaData.data ?? {};

		const initialDrawerInfo = {
			title: '',
			addNewCreditorVisibility: '',
			addCreditorAuthInfoVisibility: ''
		};
		const [drawerInfo, setDrawerInfo] =
			useState<any>(initialDrawerInfo);


		const [isVisible, setVisibility] = useState<boolean>(false);
		const [editorContent, setEditorContent] = useState(creditorGroupData?.creditorAuthorizedInformation || "");

		const columns: any = [
			{
				title: t(
					'US.COLLECTION.CREDITOR:SELECTEDCREDITOR.CREDITOR_NUMBER'
				),
				dataIndex: 'id',
				key: 'id',
				className: 'text-nowrap',
				customSorter: (a: any, b: any) => a - b,
				customFilter: true,
				customRenderChild: (text: any, record: any) => {
					return (
						<div>
							{!isCreditorManagement && (
								<Link
									to={{
										...location,
										pathname: `/creditor/${record?.pid}`,
										state: {
											...state,
											currentTab: 'creditor',
											creditorId: record?.creditorId,
										},
									}}>
									{text >
									0
										? text
										: ''}
								</Link>
							)}
							{isCreditorManagement &&
							text > 0
								? text
								: ''}
						</div>
					);
				},
			},
			{
				title: t(
					'US.COLLECTION.CREDITOR:SELECTEDCREDITOR.CREDITOR_NAME'
				),
				dataIndex: 'creditorName',
				key: 'creditorName',
				className: 'text-nowrap',
				customSorter: (a: any, b: any) =>
					a.localeCompare(b),
				customFilter: true,
			},
			{
				title: t(
					'US.COLLECTION.CREDITOR:SELECTEDCREDITOR.NIN'
				),
				dataIndex: 'nin',
				key: 'nin',
				className: 'text-nowrap',
				customSorter: (a: any, b: any) => a - b,
				customFilter: true,
			},
			{
				title: t(
					'US.COLLECTION.CREDITOR:SELECTEDCREDITOR.ADDRESS'
				),
				dataIndex: 'address',
				key: 'address',
				className: 'text-nowrap',
				customSorter: (a: any, b: any) =>
					a.localeCompare(b),
				customFilter: true,
			},
		];

		const drawerClose = () => {
			setDrawerInfo(initialDrawerInfo);			
		};

		const onMinimize = () => {
			push({
				...location,
				pathname: `/creditor-group/${creditorGroupId}`,
			});
		};

		const handleSort: IOnSort = (sortData, dataSource) => {
			return sortData(dataSource);
		};

		const handleFilter: IOnFilter = (searchData, dataSource) => {
			return searchData(dataSource);
		};


		const saveCreditorAuthorizedInformation = (authorizedInformation: string, overrideLeval: number) => {
			const payload = SaveCreditorAuthorizedInformation.call({ creditorGroupData, authorizedInformation, overrideLeval })
			addCreditorAuthorizedInformation && addCreditorAuthorizedInformation({ payload, creditorGroupData })
			drawerClose()
		};


		return (
			<Formik
				enableReinitialize
				initialValues={{
					creditorAuthorizedInformation: editorContent || ""
				}}
				onSubmit={() => {}}>
				{({values,
				...rest
				}) => (
					<>
						<div className='space-content selected-creditor'>
							{!isCreditorManagement && (
								<$Affix
									offsetTop={
										80
									}>
									<div className='page-header header-border'>
										<div className='d-flex flex-row align-items-center justify-content-between'>
											<div className='d-flex align-items-center'>
												{params[
													'module'
												] ===
													'selected-creditors' && (
													<$PageHeader
														className='px-0'
														title={t(
															'US.COLLECTION.CREDITOR:SELECTEDCREDITOR.SELECTED_CREDITOR'
														)}
														onBack={
															onMinimize
														}
													/>
												)}
												{!params[
													'module'
												] && (
													<$PageHeader
														className='px-0'
														title={t(
															'US.COLLECTION.CREDITOR:SELECTEDCREDITOR.SELECTED_CREDITOR'
														)}
													/>
												)}
												<$Button
													type='default'
													size='small'
													className='mr-2'
													icon={
														<PlusOutlined />
													}
													data-testid='add-new-creditor'
													onClick={() =>
														setDrawerInfo(
															{
																title: t(
																	'US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.NEW_CREDITOR'
																),
																addNewCreditorVisibility: true,
																addCreditorAuthInfoVisibility: false
															}
														)
													}>
													{t(
														'US.COLLECTION.CREDITOR:SELECTEDCREDITOR.NEW_CREDITOR'
													)}
												</$Button>
												<$Button
													type='default'
													size='small'
													className='mr-2'
													icon={
														<PlusOutlined />
													}
													data-testid='add-creditor-authorized-information'
													onClick={() => {
														setDrawerInfo(
															{
																title: t(
																	'US.COLLECTION.COMMON:COMMON.CREDITOR_AUTHORIZED_INFORMATION'
																),
																addNewCreditorVisibility: false,
																addCreditorAuthInfoVisibility: true
															}
														)
														setEditorContent(creditorGroupData?.creditorAuthorizedInformation)
													}
													}>
													{t(
														'US.COLLECTION.COMMON:COMMON.CREDITOR_AUTHORIZED_INFORMATION'
													)}
												</$Button>
											</div>
										</div>
									</div>
								</$Affix>
							)}

							<$Skeleton
								loading={
									selectedCreditors?.isFetching
								}
								active
								paragraph={{
									rows: 2,
								}}>
								<$TableTree
									rowKey={(
										record: any
									) =>
										record.creditorId
									}
									data={
										selectedCreditors?.data
									}
									size='small'
									className='mt-3'
									onSort={
										handleSort
									}
									onFilter={
										handleFilter
									}
									filterOnType={
										true
									}
									resetOnSourceChange={
										true
									}
									bordered
									pagination={{
										defaultPageSize: 15,
									}}
									scroll={{
										x: 1200,
									}}
									columns={
										columns
									}
									firstColSkipFilterProps={
										-1
									}
									onRow={(
										record: any
									) => {
										return {
											onDoubleClick:
												() => {
													if (
														isCreditorManagement
													) {
														drawerContentChangeClick(
															{
																record,
															}
														);
													}
												},
										};
									}}
								/>
							</$Skeleton>
						</div>
						<$Drawer
							title={drawerInfo.title}
							width={1200}
							visible={
								drawerInfo.addNewCreditorVisibility
							}
							onClose={drawerClose}
							destroyOnClose>
							<ManageCreditorInformation
								isDrawer={true}
								onClose={
									drawerClose
								}
								creditorGroupData={
									creditorGroupData
								}
								isGroupDisabled={
									true
								}
							/>
						</$Drawer>
						<$Drawer
							title={drawerInfo.title}
							width={800}
							visible={drawerInfo.addCreditorAuthInfoVisibility}
							onClose={() => {
								drawerClose()
								setVisibility(false)
							}}
							destroyOnClose
						>
							<$TextEditor
								name="creditorAuthorizedInformation"
								value={editorContent}
								modules={CreditorInfoTextEditorModules}
								handleChange={(content: any) =>
									setEditorContent(content)
								}
								onFocus={() => {
									setVisibility(true)
								}}
								style={{ height: "calc(100vh - 200px)" }}
							/>
							<div className="drawer-footer-fixed align-content-center justify-content-end">
								<div>
									<$Popconfirm
										title={t(
											"US.COLLECTION.COMMON:COMMON.CREDITOR_AUTHORIZED_INFORMATION_SAVE"
										)}
										placement="topLeft"
										onConfirm={() => {
											rest.setFieldValue("creditorAuthorizedInformation", editorContent, false);
											saveCreditorAuthorizedInformation(editorContent, CreditorGroupAuthInfoOverrideLeval.ALL_CREDITORS_IN_CREDITOR_GROUP);
											setVisibility(false);
										}}
										onCancel={() => {
											rest.setFieldValue("creditorAuthorizedInformation", editorContent, false);
											saveCreditorAuthorizedInformation(editorContent, CreditorGroupAuthInfoOverrideLeval.ONLY_APPLICABLE_CREDITORS_IN_CREDITOR_GROUP);
											setVisibility(false);
										}
										}
										okText={t("US.COMMON:COMMON.YES")}
										cancelText={t("US.COMMON:COMMON.NO")}
									>
										<$Button className=" ml-3 mr-2" disabled={!isVisible} type='primary'>{t("US.COLLECTION.COMMON:COMMON.SAVE")}</$Button>
									</$Popconfirm>
									<$Button onClick={() => {
										drawerClose()
										setVisibility(false)
									}}>
										{t("US.COLLECTION.COMMON:COMMON.CANCEL")}
									</$Button>
								</div>
							</div>
						</$Drawer>


					</>
				)}
			</Formik>
		);
	});

const mapStateToProps = (state: RootState) => {
	const { common, creditorGroup, domainView } = state;
	const { currentCurrency, currentLanguage, currentDateFormat } = common;
	const { selectedCreditors, creditorGroupDetails } = creditorGroup;
	const { metaData } = domainView;

	return {
		currentCurrency,
		currentLanguage,
		currentDateFormat,
		selectedCreditors,
		metaData,
		creditorGroupData: creditorGroupDetails?.data,
	};
};
const { selectedCreditors } = Actions;
const { creditorAuthorizedInformation } = selectedCreditors;
const mapDispatchToProps = {
	addCreditorAuthorizedInformation: creditorAuthorizedInformation.save,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(SelectedCreditorsHome);
